import React, { useState } from 'react';
import { toast } from 'sonner';
import { erpCreate, erpUpdate } from '../../../../services/erpApi';
import { Input, Textarea } from '../../ui';
import { Picker } from '../../components/Picker';
import { DocForm, Section, ItemBlocks, TotalsBar } from '../../components/DocForm';
import { emptyLine, linesPayload } from '../../components/LineItemsEditor';
import { useMasters, partyOpts } from '../../components/useMasters';

const today = () => new Date().toISOString().slice(0, 10);

// Shared Quotation / Sales Order form. kind: 'quotation' | 'so'. initial: doc to edit (quotation only).
export const SalesDocForm = ({ kind, initial, onClose, onSaved }) => {
  const { customers, skus, companies } = useMasters(['customers', 'skus', 'companies']);
  const isQt = kind === 'quotation';
  const [form, setForm] = useState(() => initial
    ? { company_id: initial.company_id, customer_id: initial.customer_id, date: initial.date, valid_till: initial.valid_till || '', customer_po_no: initial.customer_po_no || '', notes: initial.notes || '' }
    : { company_id: '', customer_id: '', date: today(), valid_till: '', customer_po_no: '', notes: '' });
  const [lines, setLines] = useState(() => (initial ? initial.items.map((l) => ({ key: l.id, ...l })) : [emptyLine()]));
  const [busy, setBusy] = useState(false);
  const dirty = !!(form.customer_id || form.notes || form.customer_po_no || lines.some((l) => l.sku_id));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const customer = customers.find((c) => c.id === form.customer_id);

  const submit = async () => {
    if (!linesPayload(lines).length) return toast.error('Add at least one item');
    setBusy(true);
    try {
      const body = { ...form, company_id: form.company_id || undefined, items: linesPayload(lines) };
      const r = initial ? await erpUpdate('quotations', initial.id, body) : await erpCreate(isQt ? 'quotations' : 'sales-orders', body);
      toast.success(`${r.data.number} ${initial ? 'updated' : 'created'}`); onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const tid = isQt ? 'qt-form' : 'so-form';

  return (
    <DocForm open onClose={onClose} dirty={dirty} busy={busy} onSubmit={submit} testid={tid}
      title={initial ? `Edit Quotation — ${initial.number}` : isQt ? 'New Quotation' : 'New Sales Order'}
      subtitle={isQt ? 'Price offer to customer · GST computed per line' : 'Lines go to management for approval before invoicing'}
      submitLabel={initial ? 'Update Quotation' : isQt ? 'Create Quotation' : 'Create Sales Order'} footerLeft={<TotalsBar lines={lines} />}>
      <Section title="Customer & Document">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {companies.length > 1 && <Picker label="Company" value={form.company_id} onChange={(v) => setForm((f) => ({ ...f, company_id: v }))} options={companies.map((c) => ({ value: c.id, label: c.name, sub: c.state }))} placeholder="Default company" testid={`${tid}-company`} />}
          <Picker label="Customer" required value={form.customer_id} onChange={(v) => setForm((f) => ({ ...f, customer_id: v }))} options={partyOpts(customers)} placeholder="Type to search customers…" testid={`${tid}-customer`} className={companies.length > 1 ? '' : 'sm:col-span-2'} />
          <Input label={isQt ? 'Quotation date' : 'Order date'} type="date" required value={form.date} onChange={set('date')} data-testid={`${tid}-date`} />
          {isQt ? <Input label="Valid till" type="date" value={form.valid_till} onChange={set('valid_till')} data-testid={`${tid}-valid`} />
            : <Input label="Customer PO number" value={form.customer_po_no} onChange={set('customer_po_no')} placeholder="PO/2026/0912" data-testid={`${tid}-po`} hint="Duplicate PO for same customer is blocked" />}
        </div>
        {customer && (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs rounded-lg px-3 py-2" style={{ background: 'var(--erp-blue-soft)', color: '#2b365a' }} data-testid={`${tid}-customer-info`}>
            <span><b>{customer.code}</b></span><span>GSTIN {customer.gstin || '—'}</span><span>State {customer.state || '—'}</span><span>Credit limit {customer.credit_limit ? `₹${Number(customer.credit_limit).toLocaleString('en-IN')}` : '—'}</span><span>{customer.contact || ''} {customer.phone || ''}</span>
          </div>
        )}
      </Section>
      <Section title="Line Items" hint="Last 3 prices for this customer appear under each item">
        <ItemBlocks lines={lines} onChange={setLines} skus={skus} customerId={form.customer_id} testid={`${tid}-items`} />
      </Section>
      <Section title="Terms & Notes">
        <Textarea value={form.notes} onChange={set('notes')} placeholder="Delivery 2 weeks · Payment 30 days · Freight extra" data-testid={`${tid}-notes`} />
      </Section>
    </DocForm>
  );
};
